import type { Src, TPlayer } from 'video.js'
import type { AudioGroup } from '|>/shared/types/audio'
import type { XhrError, XhrRequest, XhrResponse } from '|>/shared/types/errors'

import videojs from 'video.js'
import { Events } from '|>/shared/events'
import { getXhr } from '|>/shared/utils/vhs'
import { parseM3u8ForAtmosAudio, parseMpdForAtmosAudio } from '../lib'

//
// Video.js plugins guide: https://videojs.com/guides/plugins/
//

// base plugin constructor
const BasePlugin: Src.Plugin = videojs.getPlugin('plugin') as Src.Plugin

/**
 * Dolby plugin options
 */
export interface DolbyPluginOptions {}

/**
 * Dolby plugin
 */
export class DolbyPlugin extends BasePlugin {
  declare player: TPlayer

  options?: DolbyPluginOptions

  tracks?: AudioGroup[]

  constructor(player: TPlayer, options?: DolbyPluginOptions) {
    super(player)
    this.options = options

    player.addClass('vjs-dolby')

    player.on('xhr-hooks-ready', this.enable)

    player.on(Events.Media.Load, () => {
      this.tracks = undefined
    })

    player.on(Events.Media.DolbyTracks, (_, tracks) => {
      this.tracks = tracks
    })

    /* Add access to dolby instance from player instance */
    ;(player as any).dolbyPlugin = this
  }

  enable = () => {
    getXhr(this.player, 'Dolby plugin')?.onResponse(this.xhrRequestHook)
  }

  disable = () => {
    getXhr(this.player, 'Dolby plugin')?.offResponse(this.xhrRequestHook)
  }

  xhrRequestHook = (
    request: XhrRequest,
    error: XhrError | null,
    response: XhrResponse
  ) => {
    let foundTracks = this.tracks
    // only first, master manifest parsed, empty array considered as 'no dolby tracks found'
    const isFirstManifest = !this.tracks

    if (isFirstManifest && !error && typeof response.body === 'string') {
      if (request.url.includes('.m3u8'))
        foundTracks = parseM3u8ForAtmosAudio(response.body)
      if (request.url.includes('.mpd'))
        foundTracks = parseMpdForAtmosAudio(response.body)
    }

    if (foundTracks !== this.tracks) {
      // new tracks found
      this.tracks = foundTracks
      this.player.trigger(Events.Media.DolbyTracks, this.tracks)
    }
  }

  override dispose() {
    this.disable()
    this.player.off('xhr-hooks-ready', this.enable)
    super.dispose()
  }
}

// register dolby plugin
videojs.registerPlugin('dolby', DolbyPlugin)
